/**
 * FIELD-001C Section C · Truthful project-date presentation.
 *
 * Single shared helper used by every project card / detail /
 * dashboard to describe a project's completion date. Ensures every
 * surface renders the same lifecycle-aware wording for the same
 * project. Preserves recorded strings (including month-only legacy
 * estimates like "March 2026") without inventing days or timezones.
 *
 * Business timezone (`Europe/London`) drives "today" comparisons so
 * an estimate of today is not treated as past until that local
 * calendar day has ended.
 */

const BUSINESS_TZ = "Europe/London";
const MONTHS = [
    "january", "february", "march", "april", "may", "june",
    "july", "august", "september", "october", "november", "december",
];

function _now(now = new Date()) {
    // Extract Y/M/D in the business timezone without shifting the
    // stored value. `toLocaleString` with an explicit timeZone gives
    // us the calendar-day interpretation we need.
    try {
        const fmt = new Intl.DateTimeFormat("en-GB", {
            timeZone: BUSINESS_TZ,
            year: "numeric", month: "2-digit", day: "2-digit",
        }).formatToParts(now);
        const map = Object.fromEntries(fmt.map((p) => [p.type, p.value]));
        return {
            year: Number(map.year),
            month: Number(map.month), // 1-based
            day: Number(map.day),
        };
    } catch {
        return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
    }
}

/** Try to parse an estimate string into a comparable shape.
 *   • ISO date (YYYY-MM-DD)              → { kind: "date", y, m, d, display }
 *   • UK ordinal date ("26th August 2026", "3rd Mar 2025", any case,
 *     ordinal suffix optional)            → { kind: "date", y, m, d, display }
 *   • Month-year token ("March 2026")    → { kind: "month", y, m, display }
 *   • Bare year token                     → { kind: "year",  y, display }
 *   • Anything else                       → { kind: "opaque", display }
 *   • Empty / undefined                   → null
 * The `display` value is the caller-visible string; we never rewrite
 * it — precision is preserved as recorded. Legacy free-text that
 * doesn't match a recognised UK format stays opaque rather than being
 * force-parsed by `new Date(…)` (which is browser-inconsistent for
 * strings like "26th august 2026" and returns Invalid Date under Node/jsdom).
 */
export function parseEstimate(raw) {
    if (raw == null) return null;
    const s = String(raw).trim();
    if (!s) return null;
    if (/^(tbd|tbc|being reviewed|under review)$/i.test(s)) {
        return { kind: "neutral", display: s };
    }
    // Prefer plain ISO date (no timezone shift).
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
        const y = Number(iso[1]); const m = Number(iso[2]); const d = Number(iso[3]);
        if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
            return { kind: "date", y, m, d, display: s };
        }
    }
    // UK day-first with ordinal suffix — "26th August 2026", "3rd Mar 2025",
    // "1 March 2027". Suffix (st/nd/rd/th) is optional. Case-insensitive.
    const uk = s.match(/^(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3,})\s+(\d{4})$/i);
    if (uk) {
        const d = Number(uk[1]);
        const idx = MONTHS.findIndex((mn) => mn.startsWith(uk[2].toLowerCase()));
        const y = Number(uk[3]);
        if (idx >= 0 && d >= 1 && d <= 31) {
            return { kind: "date", y, m: idx + 1, d, display: s };
        }
    }
    // Month-name + year (no day).
    const my = s.match(/^([A-Za-z]{3,})\s+(\d{4})$/);
    if (my) {
        const idx = MONTHS.findIndex((mn) => mn.startsWith(my[1].toLowerCase()));
        const y = Number(my[2]);
        if (idx >= 0) return { kind: "month", y, m: idx + 1, display: s };
    }
    // Bare year.
    const y = s.match(/^(20\d{2})$/);
    if (y) return { kind: "year", y: Number(y[1]), display: s };
    // Deliberately DO NOT fall through to `new Date(s)` — permissive
    // parsing invents dates from opaque strings and is browser-
    // inconsistent. Preserve verbatim as opaque so the UI can render
    // it without an unsupported overdue flag.
    return { kind: "opaque", display: s };
}

/** Compare a parsed estimate to "today" in the business timezone.
 * Returns "past" | "today" | "future" | "unknown".
 */
export function estimateComparedToToday(parsed, now = new Date()) {
    if (!parsed) return "unknown";
    const t = _now(now);
    switch (parsed.kind) {
        case "date": {
            if (parsed.y < t.year) return "past";
            if (parsed.y > t.year) return "future";
            if (parsed.m < t.month) return "past";
            if (parsed.m > t.month) return "future";
            if (parsed.d < t.day) return "past";
            if (parsed.d > t.day) return "future";
            return "today";
        }
        case "month": {
            if (parsed.y < t.year) return "past";
            if (parsed.y > t.year) return "future";
            if (parsed.m < t.month) return "past";
            return "future"; // includes the current month — not past until it ends
        }
        case "year": {
            if (parsed.y < t.year) return "past";
            if (parsed.y > t.year) return "future";
            return "future";
        }
        default: return "unknown";
    }
}

function _lifecycleState(project) {
    return String(project?.state || "").toLowerCase();
}

function _completedAt(project) {
    if (!project) return null;
    // Prefer the authoritative lifecycle completion timestamp, then
    // legacy `completed_at`. Never fall back to updated-at or
    // arbitrary task timestamps.
    const raw = project.completed_at || project.completion_at || null;
    if (!raw) return null;
    const s = String(raw).trim();
    if (!s) return null;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;
    return d;
}

function _formatUkDate(d) {
    try {
        return d.toLocaleDateString("en-GB", {
            timeZone: BUSINESS_TZ,
            day: "numeric", month: "long", year: "numeric",
        });
    } catch {
        return d.toDateString();
    }
}

/** Build the completion-date label for a project.
 * Returns `{ text, tone, testid }`:
 *   • tone === "past"     — active project whose estimate has passed
 *   • tone === "neutral"  — TBD / recorded estimate in future / cancelled
 *   • tone === "success"  — completed with a recorded actual date
 *   • tone === "warning"  — completed WITHOUT an actual date
 *   • tone === "opaque"   — unparseable legacy text; shown verbatim
 */
export function projectCompletionLabel(project, now = new Date()) {
    if (!project) return { text: "TBD", tone: "neutral", testid: "date-empty" };
    const state = _lifecycleState(project);
    if (state === "completed") {
        const d = _completedAt(project);
        if (d) return { text: `Completed on ${_formatUkDate(d)}`, tone: "success", testid: "date-completed" };
        return { text: "Completion date not recorded", tone: "warning", testid: "date-completed-missing" };
    }
    if (state === "cancelled" || state === "canceled") {
        // Cancelled projects never surface a past-estimate warning.
        return { text: "Cancelled", tone: "neutral", testid: "date-cancelled" };
    }
    const parsed = parseEstimate(project.estimated_completion);
    if (!parsed) return { text: "Estimated completion: TBD", tone: "neutral", testid: "date-tbd" };
    if (parsed.kind === "neutral") {
        return { text: `Estimated completion: ${parsed.display}`, tone: "neutral", testid: "date-tbd" };
    }
    if (parsed.kind === "opaque") {
        // Unparseable legacy text — preserve verbatim, no overdue flag.
        return { text: `Estimated completion: ${parsed.display}`, tone: "opaque", testid: "date-opaque" };
    }
    const rel = estimateComparedToToday(parsed, now);
    if (rel === "past") {
        return {
            text: `Estimate passed — review required (was ${parsed.display})`,
            tone: "past",
            testid: "date-past-estimate",
        };
    }
    return { text: `Estimated completion: ${parsed.display}`, tone: "neutral", testid: "date-future-estimate" };
}

/** Short caption used inside cramped meta chips ("Est. completion · …").
 * Same rules; the caller supplies the leading label so we only render the
 * value.
 */
export function projectCompletionValue(project, now = new Date()) {
    if (!project) return { text: "TBD", tone: "neutral", testid: "date-empty" };
    const state = _lifecycleState(project);
    if (state === "completed") {
        const d = _completedAt(project);
        if (d) return { text: _formatUkDate(d), tone: "success", testid: "date-completed" };
        return { text: "Not recorded", tone: "warning", testid: "date-completed-missing" };
    }
    if (state === "cancelled" || state === "canceled") {
        return { text: "Cancelled", tone: "neutral", testid: "date-cancelled" };
    }
    const parsed = parseEstimate(project.estimated_completion);
    if (!parsed) return { text: "TBD", tone: "neutral", testid: "date-tbd" };
    if (parsed.kind === "neutral") return { text: parsed.display, tone: "neutral", testid: "date-tbd" };
    if (parsed.kind === "opaque") return { text: parsed.display, tone: "opaque", testid: "date-opaque" };
    const rel = estimateComparedToToday(parsed, now);
    if (rel === "past") {
        return { text: `${parsed.display} · estimate passed`, tone: "past", testid: "date-past-estimate" };
    }
    return { text: parsed.display, tone: "neutral", testid: "date-future-estimate" };
}

/** Truthful stage caption. Backend's `current_phase_info` returns
 * "Complete" when `phases` is empty; on an active project that is
 * a data hole, not a completion signal. This helper falls back to
 * "Stage not recorded" so we never render "Complete / All tasks
 * complete" against a 0% active project.
 */
export function projectStageLabel(project) {
    if (!project) return "Stage not recorded";
    const state = _lifecycleState(project);
    if (state === "completed") return project.current_phase || "Complete";
    if (state === "cancelled" || state === "canceled") return project.current_phase || "Cancelled";
    const phases = Array.isArray(project.phases) ? project.phases : null;
    const rawPhase = project.current_phase;
    const noRealPhases = phases !== null && phases.length === 0;
    const looksLikeStub = !rawPhase
        || rawPhase === "Complete"
        || rawPhase === "Complete (data mismatch)";
    if (noRealPhases && looksLikeStub) return "Stage not recorded";
    return rawPhase || "Stage not recorded";
}

/** Truthful next-milestone caption — mirrors `projectStageLabel`.
 * When a non-terminal project has no phases, backend can emit the
 * "All tasks complete" stub which contradicts 0% progress. Return
 * "Not recorded" instead so the UI stops implying completion.
 */
export function projectNextMilestone(project) {
    if (!project) return "Not recorded";
    const state = _lifecycleState(project);
    if (state === "completed") return project.next_milestone || "Complete";
    const raw = project.next_milestone;
    const phases = Array.isArray(project.phases) ? project.phases : null;
    const noRealPhases = phases !== null && phases.length === 0;
    const stubbyClaim = /^all tasks complete$/i.test(String(raw || ""));
    if (state !== "completed" && (stubbyClaim || (noRealPhases && !raw))) {
        return "Not recorded";
    }
    return raw || "Not recorded";
}

/** Combined `{label, value, tone, testid}` field for the completion
 * date. Emits both parts from a single source so a completed project
 * can never end up displaying "Est. completion: Not recorded" — the
 * label itself changes with lifecycle.
 */
export function projectCompletionField(project, now = new Date()) {
    if (!project) {
        return { label: "Estimated completion", value: "TBD", tone: "neutral", testid: "date-empty" };
    }
    const state = _lifecycleState(project);
    if (state === "completed") {
        const d = _completedAt(project);
        if (d) return { label: "Completed on", value: _formatUkDate(d), tone: "success", testid: "date-completed" };
        return { label: "Completion date", value: "Not recorded", tone: "warning", testid: "date-completed-missing" };
    }
    if (state === "cancelled" || state === "canceled") {
        return { label: "Status", value: "Cancelled", tone: "neutral", testid: "date-cancelled" };
    }
    const parsed = parseEstimate(project.estimated_completion);
    if (!parsed) return { label: "Estimated completion", value: "TBD", tone: "neutral", testid: "date-tbd" };
    if (parsed.kind === "neutral") {
        return { label: "Estimated completion", value: parsed.display, tone: "neutral", testid: "date-tbd" };
    }
    if (parsed.kind === "opaque") {
        return { label: "Estimated completion", value: parsed.display, tone: "opaque", testid: "date-opaque" };
    }
    const rel = estimateComparedToToday(parsed, now);
    if (rel === "past") {
        return {
            label: "Estimated completion",
            value: `${parsed.display} · estimate passed — review required`,
            tone: "past",
            testid: "date-past-estimate",
        };
    }
    return { label: "Estimated completion", value: parsed.display, tone: "neutral", testid: "date-future-estimate" };
}
